import { fetchVendorsProducts } from '@/apis';
import Cookies from 'js-cookie';

export const VENDORS_PRODUCTS_KEY = 'fbc-vendors-products-data';
export const VENDORS_PRODUCTS_TIMESTAMP_KEY = 'fbc-vendors-products-timestamp';
export const CACHE_EXPIRY_TIME = 2 * 60 * 60 * 1000;

// Create a reactive store structure
let vendorsProductsStore = {
  state: {
    vendors: [],
    products: [],
  },
  errorCode: null,
  update(newState) {
    this.state = newState;
    this.errorCode = null;
  },
  setError(errorCode) {
    this.errorCode = errorCode;
  }
};

export { vendorsProductsStore };

const isValidData = (data) => {
  return data.vendors && data.vendors.length > 0 && data.products && data.products.length > 0;
};

const isCacheExpired = () => {
  try {
    const cacheInfo = sessionStorage.getItem(VENDORS_PRODUCTS_TIMESTAMP_KEY);
    if (!cacheInfo) {
      return true;
    }

    const { timestamp, language } = JSON.parse(cacheInfo);
    const currentLanguage = Cookies.get('_zm_lang') || 'en-US';
    
    // Check if language changed
    if (language !== currentLanguage) {
      console.log(`VENDORS_PRODUCTS - Language changed from ${language} to ${currentLanguage}, cache expired`);
      return true;
    }
    
    // Check if time expired
    const now = Date.now();
    const isTimeExpired = now - timestamp > CACHE_EXPIRY_TIME;
    if (isTimeExpired) {
      console.log('VENDORS_PRODUCTS - Time expired, cache expired');
    }
    
    return isTimeExpired;
  } catch (error) {
    console.error('VENDORS_PRODUCTS - Check cache expiry error:', error);
    return true;
  }
};

const setCacheData = (data) => {
  try {
    const currentLanguage = Cookies.get('_zm_lang') || 'en-US';
    const cacheInfo = {
      timestamp: Date.now(),
      language: currentLanguage
    };
    
    sessionStorage.setItem(VENDORS_PRODUCTS_KEY, JSON.stringify(data));
    sessionStorage.setItem(VENDORS_PRODUCTS_TIMESTAMP_KEY, JSON.stringify(cacheInfo));
    console.log(`VENDORS_PRODUCTS - Cache saved for language: ${currentLanguage}`);
  } catch (error) {
    console.error('VENDORS_PRODUCTS - Set cache error:', error);
  }
};

/**
 * Get vendors products data
 * Cache will be invalidated if:
 * 1. Cache time exceeds CACHE_EXPIRY_TIME (2 hours)
 * 2. Language (_zm_lang cookie) changes from when cache was created
 * @param {boolean} useCacheSessionStorage - Whether to use cache, default true
 */
export const getVendorsProducts = async (useCacheSessionStorage = true) => {
  if (!useCacheSessionStorage) {
    try {
      const result = await fetchVendorsProducts();
      if (isValidData(result)) {
        setCacheData(result);
        vendorsProductsStore.update(result);
      }

      return result;
    } catch (error) {
      console.error('VENDORS_PRODUCTS - API error:', error);
      vendorsProductsStore.setError(error.response?.status || error.code || 500);
      throw error;
    }
  }

  if (vendorsProductsStore && isValidData(vendorsProductsStore.state) && !isCacheExpired()) {
    return vendorsProductsStore.state;
  }

  try {
    const storedValue = sessionStorage.getItem(VENDORS_PRODUCTS_KEY);
    if (storedValue && !isCacheExpired()) {
      const cachedData = JSON.parse(storedValue);
      if (isValidData(cachedData)) {
        vendorsProductsStore.update(cachedData);
        return cachedData;
      }
    }
  } catch (error) {
    sessionStorage.removeItem(VENDORS_PRODUCTS_KEY);
    sessionStorage.removeItem(VENDORS_PRODUCTS_TIMESTAMP_KEY);
    console.error('VENDORS_PRODUCTS - Cache error:', error);
  }

  try {
    const result = await fetchVendorsProducts();
    
    if (isValidData(result)) {
      setCacheData(result);
      vendorsProductsStore.update(result);
    }
    
    return result;
  } catch (error) {
    console.error('VENDORS_PRODUCTS - API error:', error);
    vendorsProductsStore.setError(error.response?.status || error.code || 500);
    throw error;
  }
};

/**
 * Clear vendors products cache
 * Removes both data and timestamp+language cache info
 */
export const clearVendorsProductsCache = () => {
  vendorsProductsStore.update({ vendors: [], products: [] });
  sessionStorage.removeItem(VENDORS_PRODUCTS_KEY);
  sessionStorage.removeItem(VENDORS_PRODUCTS_TIMESTAMP_KEY);
};
